import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from '@mui/icons-material/Help';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  TableCell,
  TableRow,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { qrcodeAPIService } from '~/api/qrcode';
import Check from '~/assets/icons/svgs/check-with-green-background.svg?react';
import CopyIcon from '~/assets/icons/svgs/copy.svg?react';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { REDIRECT_QR_URI, TABLET_MIN_WIDTH } from '~/constants/common';
import { QR_CODE_TYPE } from '~/constants/qr-code';
import { qrCodeKeys } from '~/constants/queryKeys';
import { PATH } from '~/constants/route-path';
import useCurrentUser from '~/hooks/useCurrentUser';
import { QrItemButtons } from '~/pages/MyQR/components/QrItemButtons/QrItemButtons';
// TODO there are 2 equal imports, make 1
import * as S from '~/pages/MyQR/components/QrTable/styles';
import { StyledChecbox, StyledImage } from '~/pages/MyQR/components/QrTable/styles';
import { useToastStore } from '~/store/useToastStore';
import { copyTextToClipboard } from '~/utils/copyTextToClipboard';
import { getUserDateTimeFormat } from '~/utils/getUserDateTimeFormat';

import Calendar from '../../assets/calendar.svg?react';
import Folder from '../../assets/folder.svg?react';
import LinkIcon from '../../assets/link.svg?react';
import Pen from '../../assets/pen.svg?react';

const typeMap = {
  pdf: 'PDF',
  app: 'Apps',
  website: 'Website',
  links: 'List of Links',
  'social-media': 'Social media',
  'vcard-plus': 'vCard Plus',
  url: 'URL',
  wifi: 'WI-FI',
  vcard: 'vCard',
  sms: 'SMS',
  images: 'Images',
  coupon: 'Coupon',
  video: 'Video',
};

type QrTableRowItemProps = {
  row: any;
  checked: any[];
  setChecked: (value: any) => void;
  onDetailClick: (id: any, label: string) => void;
  setDeleteItemsModal: (value: any[]) => void;
  setDownloadByIdModal: (value: { id?: string; name: string }) => void;
  setImageModal: (value: any) => void;
  currentFetchParams: any;
};
export default function QrTableRowItem({
  row,
  checked,
  setChecked,
  setDeleteItemsModal,
  onDetailClick,
  setDownloadByIdModal,
  setImageModal,
  currentFetchParams,
}: QrTableRowItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));

  const { setShow } = useToastStore();

  const [showInputForName, setShowInputForName] = useState(false);
  const [newName, setNewName] = useState(row?.name ?? '');

  const isChecked = !!checked.find((item) => item.id === row.id);
  const isDynamicQr = row.type === QR_CODE_TYPE.DYNAMIC;
  const qrTypeName = row?.content?.type ? row?.content?.type : row?.micrositeType;
  const mappedName = typeMap[qrTypeName?.toLowerCase()];
  const getLinkHref = () => {
    let baseLink = row.type === 'STATIC' ? row.content.url : `${REDIRECT_QR_URI}${PATH.S(row.uri)}`;
    if (row.isPasswordProtected && row.type !== 'STATIC') {
      baseLink += '?password_required=true';
    }
    return baseLink;
  };
  const linkHref = getLinkHref();

  const { data: userDateTimeFormat } = useCurrentUser({
    select: (response) => {
      return {
        hoursFormat: response.hoursFormat,
        dateFormat: response.dateFormat,
      };
    },
  });

  const formatString = getUserDateTimeFormat(userDateTimeFormat?.dateFormat, userDateTimeFormat?.hoursFormat);
  const formattedCreatedAt = format(new Date(row.createdAtRaw), formatString);

  let dynamicLinkBody = '';

  if (row.type === 'STATIC') {
    dynamicLinkBody = row.content.url;
  } else {
    dynamicLinkBody = row.micrositeType === 'website' ? row?.microsite?.url : `qbn.sh${PATH.S(row.uri)}`;
  }

  const queryClient = useQueryClient();
  const { mutate: updateQrName, isPending: isPendingQrNameUpdate } = useMutation({
    mutationFn: async (newName: string) => {
      return await qrcodeAPIService.updateQrCode({ name: newName }, row.id);
    },
    onSuccess: async (response, newName) => {
      const key = [...qrCodeKeys.myQrsData, currentFetchParams];

      const previousData = queryClient.getQueryData(key);

      try {
        queryClient.setQueryData(key, (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              results: oldData.data.results.map((item: any) =>
                item.id === row.id ? { ...item, name: newName } : item,
              ),
            },
          };
        });
      } catch (error) {
        void queryClient.invalidateQueries({
          queryKey: key,
        });
      }

      setNewName(newName);
      setShowInputForName(false);
      return { previousData }; // so you can rollback on error
    },
    onError: (err, newName, context: any) => {
      setNewName(row.name ?? '');
      if (context?.previousData) {
        queryClient.setQueryData([qrCodeKeys.myQrsData, currentFetchParams], context.previousData);
      }
    },
  });

  const getChipStatus = (row) => {
    if (row.isAvailableLimit === false) {
      return { label: t('qrTable:scanLimit'), className: 'scan-limit-item' };
    }
    if (row.status === 'DRAFT') {
      return { label: t('qrTable:draft'), className: 'draft-item' };
    }
    if (row.isActive) {
      return {
        label: t('labels:active'),
        className: 'active-item',
      };
    }
    return {
      label: t('labels:paused'),
      className: 'paused-item',
    };
  };

  const handleCopy = async (text: string) => {
    const result = await copyTextToClipboard(text);
    if (result) {
      setShow({
        show: true,
        type: 'success',
        message: t('alerts:successCopiedToClipboard'),
      });
    }
  };

  const statusChipInfo = getChipStatus(row);

  const handleUpdateName = () => {
    if (isPendingQrNameUpdate) return;
    updateQrName(newName);
  };

  return (
    <TableRow key={row.id}>
      <TableCell sx={S.cellWrapper}>
        <StyledChecbox
          checked={isChecked}
          onClick={() =>
            setChecked((prev) =>
              isChecked
                ? prev.filter((item) => item.id !== row.id)
                : [
                    ...prev,
                    { id: row.id, type: row.type, status: row.status, folderId: row.folder ? row.folder.id : null },
                  ],
            )
          }
        />
        <ButtonBase sx={S.qrImage} onClick={() => setImageModal(row?.qrImage)}>
          <StyledImage src={row?.qrImage} alt={row.name} />
        </ButtonBase>
        <Box sx={S.rigthWrapper}>
          <Box sx={S.descriptionWrapper}>
            <Text variant="h3" sx={S.descriptionType}>
              {mappedName ?? qrTypeName}
            </Text>
            <Text variant="h3" sx={S.descriptionName}>
              {showInputForName ? (
                <TextInput
                  value={newName}
                  disabled={isPendingQrNameUpdate}
                  onChange={(event) => setNewName(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Box display="flex" alignItems="center">
                        {isPendingQrNameUpdate ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Check
                            onClick={() => handleUpdateName()}
                            style={{ width: '16px', flexShrink: 0, cursor: 'pointer' }}
                          />
                        )}
                      </Box>
                    ),
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              ) : (
                <>
                  <Tooltip
                    title={t('buttons:edit')}
                    slotProps={{
                      tooltip: {
                        sx: S.customTooltip,
                      },
                    }}
                  >
                    <IconButton
                      sx={{ flexShrink: 0, cursor: 'pointer', padding: 0 }}
                      onClick={() => setShowInputForName(true)}
                    >
                      <Pen />
                    </IconButton>
                  </Tooltip>
                  {row.name ? row.name : t('qrTable:untitled')}
                </>
              )}
            </Text>
            {!isMobile && (
              <Text variant="input15" sx={S.descriptionDate}>
                <Tooltip
                  title={t('createQrStep1:created')}
                  slotProps={{
                    tooltip: {
                      sx: S.customTooltip,
                    },
                  }}
                >
                  <IconButton sx={{ padding: 0, cursor: 'initial' }}>
                    <Calendar />
                  </IconButton>
                </Tooltip>
                {formattedCreatedAt}
              </Text>
            )}
          </Box>
          {!isMobile && (
            <Box display="flex" flexDirection="column" maxWidth="190px" width="100%" justifyContent="center" gap="8px">
              {linkHref && (
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Link href={linkHref} target="_blank" sx={S.folderLink}>
                    <LinkIcon />
                    <span>{dynamicLinkBody}</span>
                  </Link>
                  <Box
                    component={CopyIcon}
                    onClick={() => handleCopy(linkHref)}
                    sx={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                </Box>
              )}
              <Text variant="input15" sx={S.folderName}>
                <Folder />
                {row.folder?.name ? row.folder.name : t('qrTable:noFolder')}
              </Text>
            </Box>
          )}
          <Box sx={S.qrType} className={isDynamicQr ? 'is-dynamic-qr' : ''}>
            {isDynamicQr ? (
              <Box sx={S.dynamicQrText}>
                <Text variant={isMobile ? 'inputSmall' : 'input15'} sx={{ color: 'var(--orange2)' }}>
                  {t('labels:scans')}
                </Text>
                <Text variant={isMobile ? 'button14Med' : 'h1.7'}> {row?.statistic?.totalScans || 0} </Text>
              </Box>
            ) : (
              <Chip
                sx={S.chip}
                label={
                  <>
                    <Text variant="input15" sx={{ color: '#A48D08' }}>
                      {row.type}
                    </Text>
                    <Tooltip
                      slotProps={{
                        tooltip: {
                          sx: S.customTooltip,
                        },
                      }}
                      title={t('labels:staticTootip')}
                    >
                      <HelpIcon sx={S.helpIcon} fontSize="small" />
                    </Tooltip>
                  </>
                }
              />
            )}
          </Box>
          <Chip sx={S.status} className={statusChipInfo.className} label={statusChipInfo.label} />
          <QrItemButtons
            row={row}
            onDetail={() => onDetailClick(row.id, `Landing Page ${row.id}`)}
            onDelete={() => setDeleteItemsModal([row?.id])}
            onDownload={() => setDownloadByIdModal({ id: row?.id, name: row?.name })}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}
